'use client';

import { useLocation, Link } from 'react-router-dom';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MasterAccountService } from '@/core/services/masterAccountService';
import { ExchangeService } from '@/core/services/exchangeService';
import { DcaService } from '@/core/services/dcaService';
import { MandateService } from '@/core/services/mandateService';
import { WalletService } from '@/core/services/walletService';
import { useMaintenanceStatus } from '@/core/components/maintenance-gate';
import { useTranslation } from 'react-i18next';

// Mapping of routes to maintenance feature keys
const ROUTE_TO_FEATURE: Record<string, string> = {
  '/main-account/': 'MAIN_ACCOUNT',
  '/exchange/': 'EXCHANGE',
  '/dca-strategy/': 'DCA',
  '/management-mandate/': 'MANDATE',
  '/treasury/': 'TREASURY',
};

interface MenuItem {
  link: string;
  name?: string;
  icon?: React.ReactNode;
  status?: string;
  isDisabled?: boolean;
}

export default function MenuLink({ item, onClick }: { item: MenuItem, onClick?: () => void }) {
  const location = useLocation();
  const pathname = location.pathname;
  const isActive = item.link === pathname;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Check if this route has a maintenance mode
  const featureKey = ROUTE_TO_FEATURE[item.link];
  const { isUnderMaintenance } = useMaintenanceStatus(featureKey || '');

  const className = `menu-link-item ${isActive ? 'active' : ''}`;

  // Prefetch data for common routes on hover
  const handlePrefetch = () => {
    if (item.link === '/main-account') {
      queryClient.prefetchQuery({
        queryKey: ['mainAccountOverview'],
        queryFn: () => MasterAccountService.getMainAccountOverview(),
      });
      queryClient.prefetchQuery({
        queryKey: ['mainAccountAllocations'],
        queryFn: () => MasterAccountService.getMainAccountAllocation(),
      });
    } else if (item.link === '/exchange') {
      queryClient.prefetchQuery({
        queryKey: ['exchangeOverview'],
        queryFn: () => ExchangeService.getExchangeOverview(),
      });
      queryClient.prefetchQuery({
        queryKey: ['exchangeAllocations'],
        queryFn: () => ExchangeService.getExchangeAllocations(),
      });
    } else if (item.link === '/dca-strategy') {
      queryClient.prefetchQuery({
        queryKey: ['dcaStrategies'],
        queryFn: () => DcaService.getAllDCAModel(),
      });
    } else if (item.link === '/management-mandate') {
      queryClient.prefetchQuery({
        queryKey: ['listMandate'],
        queryFn: () => MandateService.getListMandate(),
      });
    } else if (item.link === '/') {
      queryClient.prefetchQuery({
        queryKey: ['walletOverview'],
        queryFn: () => WalletService.getWalletOverview(),
      });
    }
  };

  // Render the maintenance badge if feature is under maintenance
  const renderMaintenanceBadge = () => {
    if (isUnderMaintenance) {
      return <span className="maintenance-badge">{t('maintenance.badge')}</span>;
    }
    return null;
  };

  return item.isDisabled ? (
    <div className={className} onClick={onClick}>
      {item.icon && <div className="menu-link-icon">{item.icon}</div>}
      {item.name && <span className="menu-link-name">{item.name}</span>}
      {renderMaintenanceBadge()}
      {item.status && <span className="menu-link-status">{item.status}</span>}
    </div>
  ) : (
    <Link to={item.link} className={className} onMouseEnter={handlePrefetch}>
      {item.icon && <div className="menu-link-icon">{item.icon}</div>}
      {item.name && <span className="menu-link-name">{item.name}</span>}
      {renderMaintenanceBadge()}
      {item.status && <span className="menu-link-status">{item.status}</span>}
    </Link>
  );
}
